import {gql} from '@apollo/client';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ProcedureChipPopoverQuery,
  ProcedureChipPopoverQuery_procedure_ProcedureNode as AccessibleProcedureNode,
  ProcedureChipPopoverQuery_procedure_ProcedureNode_publishingRecords_PublishingRecordNode as PublishingRecordNode,
  ProcedureChipPopoverQueryVariables,
} from 'procedures/chip-utils/__generated/ProcedureChipPopoverQuery';
import {
  PUBLISHABLE_VERSION_STATUS_TO_COLOR,
  PUBLISHABLE_VERSION_STATUS_TO_ICON,
} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import ChipPopoverContent, {ChipPopoverInfoField} from 'ui-library/chips/ChipPopoverContent';
import QueryingChipPopoverContent from 'ui-library/chips/QueryingChipPopoverContent';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';
import strUtil from 'util/strUtil';

export const PROCEDURE_CHIP_POPOVER_QUERY = gql`
  query ProcedureChipPopoverQuery($procedureId: StrictString!) @skipAudit @engComponent(name: PROCEDURES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        __typename
        id
        name
        modifiedAt
        creator {
          id
          name
          avatarUrl
        }
        publishingRecords {
          ... on PublishingRecordNode {
            id
            publishingStatus
            versionName
            versionDescription
            publishableVersionId
            publishedAt
            publishedByUser {
              id
              name
              avatarUrl
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getFieldForModifiedAt(modifiedAt: string): ChipPopoverInfoField {
  return {
    label: 'Last modified',
    value: readableTime(modifiedAt),
  };
}

function getCreatorField(creator: HasAvatar): ChipPopoverInfoField {
  return {
    label: 'Creator',
    value: (
      <AvatarWithName
        size={AvatarSize.XXSMALL}
        shape={AvatarShape.CIRCLE}
        avatarInfo={creator}
        showTooltip={false}
      />
    ),
  };
}

function getChipPopoverFields(
  procedure: AccessibleProcedureNode,
  publishingRecordForVersion?: PublishingRecordNode
): Array<ChipPopoverInfoField> {
  const procedureName: ChipPopoverInfoField = {
    label: 'Name',
    value: procedure.name,
  };

  if (publishingRecordForVersion == null) {
    return [procedureName, getCreatorField(procedure.creator), getFieldForModifiedAt(procedure.modifiedAt)];
  }

  const {publishingStatus, versionName, versionDescription} = publishingRecordForVersion;
  return [
    procedureName,
    {
      label: 'Version',
      value: versionDescription ? (
        <p>
          {versionName}
          <br />
          {versionDescription}
        </p>
      ) : (
        versionName
      ),
    },
    {
      label: 'Status',
      value: (
        <>
          <BenchlingIcon
            icon={PUBLISHABLE_VERSION_STATUS_TO_ICON[publishingStatus]}
            style={{color: PUBLISHABLE_VERSION_STATUS_TO_COLOR[publishingStatus]}}
          />{' '}
          {strUtil.toSentenceCase(publishingStatus)}
        </>
      ),
    },
    getCreatorField(publishingRecordForVersion.publishedByUser),
    getFieldForModifiedAt(publishingRecordForVersion.publishedAt),
  ];
}

/** @package */
export function ProcedureChipPopoverContentDisplay(props: {
  procedure: ProcedureChipPopoverQuery['procedure'];
  flowchartVersionId?: string;
}): JSX.Element | null {
  const {procedure} = props;

  // This popover should be hidden by ProcedureChip; this is just a safety check
  if (isInaccessible(procedure)) {
    return <>(No access)</>;
  }

  // There's no way to get the publishing record for a flowchart version directly. Instead, we have to find
  // it by scanning all the procedure's publishing records. This query is very likely to be quite small.
  const targetPublishingRecord =
    props.flowchartVersionId && BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING
      ? accessibleObjs(procedure.publishingRecords).find(
          (record) => record.publishableVersionId === props.flowchartVersionId
        )
      : undefined;

  return <ChipPopoverContent info={{fields: getChipPopoverFields(procedure, targetPublishingRecord)}} />;
}

export default function ProcedureChipPopoverContents({
  id,
  flowchartVersionId,
}: {
  id: string;
  flowchartVersionId?: string;
}): JSX.Element | null {
  return (
    <QueryingChipPopoverContent<ProcedureChipPopoverQuery, ProcedureChipPopoverQueryVariables>
      variables={{procedureId: id}}
      query={PROCEDURE_CHIP_POPOVER_QUERY}
    >
      {(data: ProcedureChipPopoverQuery) => (
        <ProcedureChipPopoverContentDisplay
          procedure={data.procedure}
          flowchartVersionId={flowchartVersionId}
        />
      )}
    </QueryingChipPopoverContent>
  );
}
